import { useMemo, useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import {
    Button,
    Container,
    Dropdown,
    OutsideClickHandler,
    Overlay,
    Spinner,
    TextField,
    Typography
} from '@cleartrip/ct-design-components'
import Swap from '@/assets/icons/Swap'
import { DownChevronNew } from '@/assets/icons/DownChevron'
import { AirportSearchType, DropdownType, LegType } from './types'
import {
    TRIP_TYPE,
    tripTypeOptions,
    ONWARD,
    RETURN,
    popularCities,
    SAME_CITY_ERROR,
    SRP_REVAMP_FIELD_WIDTH,
    TRAVELLERS
} from '@/constants/Srp'
import { debounce, isEmpty, isNewInternational } from '@/utils/general/browserHelper'
import { airportSearch } from '@/api/srp'
import { getRecentSearch, updateRecentSearch } from '@/utils/srp'
import { getFormattedDate } from '@/utils/general/date'
import { QueryParamsData } from '@/types/srp'
import DateRangePicker from '@/components/oldComponents/ModifySearch/DateRangePicker'
import Travellers from '@/components/srp/ModifySearch/Travellers'
import { PE_SRP_GLASS_FIELD_CSS } from '@/constants/flights/premiumEconomy'
import { isServer } from '@/utils'
import { isUserSignedIn } from '@/utils/general/user'
import Toast from '@/components/Toast'
import ReactDOM from 'react-dom'
import dayjs from 'dayjs'
import { STATSIG_SPLIT_RT_EXP_NAME } from '@/constants/experiment'
import { Statsig } from '@/utils/statsig'
import { FARE_TYPE, FareTypeInterface } from '@/constants/components/fareType'
import SpecialFaresRow from '@/components/shared/SpecialFaresRow'

const cityValueFrom = (display?: string, otherLeg?: string) =>
    display && otherLeg ? { code: display, name: display.split(',')[0].split('- ')[1] } : { code: '', name: '' }

const isLegInternational = (display: string | undefined, intlQuery: string | undefined) => {
    const parts = display?.split(', ') ?? []
    if (parts.length >= 2) {
        const last = parts[parts.length - 1]?.trim()
        if (/^[A-Za-z]{2}$/.test(last)) return last.toUpperCase() !== 'IN'
    }
    return intlQuery === 'y'
}

const chipSftFromQuery = (raw: unknown) => {
    const s = Array.isArray(raw) ? raw[0] : raw
    if (s == null || typeof s !== 'string') return ''
    const k = s.trim().toLowerCase().replace(/\s+/g, '_')
    if (k === 'student') return 'student'
    if (k === 'senior_citizen') return 'senior_citizen'
    if (k === 'army') return 'army'
    return ''
}

const ModifySearchWidget = ({
    searchQuery = {},
    isSearchApiLoading = false,
    searchData = {},
    onSearchClick = () => {},
    isLoading = false,
    fareType = '',
    isThemed = false,
    // First Class hero is near-black, so labels that sit directly on it need white.
    isDarkThemed = false,
    isSrpRevamp = false
}) => {
    const glassFieldCss = isThemed ? PE_SRP_GLASS_FIELD_CSS : {}
    let router = useRouter()
    let searchParams = router.query as unknown as QueryParamsData
    const [dropdownOpenType, setDropdownOpenType] = useState('')
    const [onwardCityDisplayValue, setOnwardCityDisplayValue] = useState(searchQuery?.origin || searchParams?.origin)
    const [returnCityDisplayValue, setReturnCityDisplayValue] = useState(
        searchQuery?.destination || searchParams?.destination
    )
    const [recentSearchList, setRecentSearchList] = useState<AirportSearchType[] | []>(getRecentSearch())
    const [showOnwardRecentSearchList, setShowOnwardRecentSearchList] = useState(false)
    const [showReturnRecentSearchList, setShowReturnRecentSearchList] = useState(false)
    const [suggestionLoading, setSuggestionLoading] = useState(false)
    const [swapped, setSwapped] = useState(false)
    const [showToast, setShowToast] = useState({
        isVisible: false,
        message: ''
    })
    const [onwardCityValue, setOnwardCityValue] = useState(() => cityValueFrom(searchQuery?.origin, searchQuery?.destination))
    const [returnCityValue, setReturnCityValue] = useState(() => cityValueFrom(searchQuery?.destination, searchQuery?.origin))
    const [airportSearchList, setAirportSearchList] = useState({
        [ONWARD]: popularCities,
        [RETURN]: popularCities
    })
    const [tripType, setTripType] = useState(searchQuery.return_date ? tripTypeOptions[1] : tripTypeOptions[0])
    
    const [fareList, setFareList] = useState<FareTypeInterface[]>(FARE_TYPE)
    const [activeFare, setActiveFare] = useState(searchParams?.sft || '')
    const [isCfwSelected, setIsCfwSelected] = useState(searchQuery?.isCfw || false)
    const [cfwModal, setCfwModal] = useState(false)
    const [isIntlArray, setIsIntlArray] = useState(() => {
        if (!onwardCityValue.code || !returnCityValue.code) return [false, false]
        const intlQuery = searchParams?.intl
        return [isLegInternational(onwardCityDisplayValue, intlQuery), isLegInternational(returnCityDisplayValue, intlQuery)]
    })
    const isIntl = isIntlArray[0] || isIntlArray[1]

    let {
        adults,
        childs,
        infants,
        depart_date,
        return_date,
        dx,
        intl,
        from,
        to,
        carrier,
        origin,
        destination,
        class: flightClass,
        sft
    } = searchParams

    const [query, setQuery] = useState({
        adults,
        childs,
        infants,
        depart_date,
        return_date,
        intl,
        from,
        to,
        carrier,
        origin,
        destination,
        class: flightClass,
        sft
    })

    const initialSearchValues = useRef({
        origin: searchQuery?.origin || searchParams?.origin,
        destination: searchQuery?.destination || searchParams?.destination,
        depart_date,
        return_date,
        adults,
        childs,
        infants,
        flightClass,
        tripType: searchQuery.return_date ? 'RT' : 'OW',
        activeFare: searchParams?.sft || '',
        isCfwSelected: searchQuery?.isCfw || false
    })
    const hasSearchChanges = useMemo(() => {
        return (
            onwardCityDisplayValue !== initialSearchValues.current.origin ||
            returnCityDisplayValue !== initialSearchValues.current.destination ||
            query.depart_date !== initialSearchValues.current.depart_date ||
            query.return_date !== initialSearchValues.current.return_date ||
            query.adults !== initialSearchValues.current.adults ||
            query.childs !== initialSearchValues.current.childs ||
            query.infants !== initialSearchValues.current.infants ||
            query.class !== initialSearchValues.current.flightClass ||
            tripType.key !== initialSearchValues.current.tripType ||
            activeFare !== initialSearchValues.current.activeFare ||
            isCfwSelected !== initialSearchValues.current.isCfwSelected
        )
    }, [
        onwardCityDisplayValue,
        returnCityDisplayValue,
        query.depart_date,
        query.return_date,
        query.adults,
        query.childs,
        query.infants,
        query.class,
        tripType.key,
        activeFare,
        isCfwSelected
    ])
    const isSearchButtonDisabled = isSearchApiLoading || !hasSearchChanges
    const ERROR_COLOR_CODE = '#C83232'
    const textFieldErrorColor = {
        outlineColor: ERROR_COLOR_CODE
    }
    const isDepartCityError =
        !isServer() && !isEmpty(searchQuery) && (!onwardCityValue.code || onwardCityValue.code === returnCityValue.code)
    const isArrivalCityError =
        !isServer() && !isEmpty(searchQuery) && (!returnCityValue.code || onwardCityValue.code === returnCityValue.code)
    const departCityColorCode = isDepartCityError ? textFieldErrorColor : {}
    const arrivalCityColorCode = isArrivalCityError ? textFieldErrorColor : {}

    useEffect(() => {
        if (searchQuery?.origin && searchQuery?.destination) {
            setReturnCityDisplayValue(searchQuery?.destination)
            setOnwardCityDisplayValue(searchQuery?.origin)

            setOnwardCityValue({
                code: searchQuery?.origin,
                name: searchQuery?.origin.split(',')[0].split('- ')[1]
            })
            setReturnCityValue({
                code: searchQuery?.destination,
                name: searchQuery?.destination.split(',')[0].split('- ')[1]
            })
            initialSearchValues.current = {
                origin: searchQuery?.origin,
                destination: searchQuery?.destination,
                depart_date: searchQuery?.depart_date,
                return_date: searchQuery?.return_date,
                adults: searchQuery?.adults,
                childs: searchQuery?.childs,
                infants: searchQuery?.infants,
                flightClass: searchQuery?.class,
                tripType: searchQuery.return_date ? 'RT' : 'OW',
                activeFare: searchQuery?.sft || '',
                isCfwSelected: searchQuery?.isCfw || false
            }
        }
        setTripType(searchQuery.return_date ? tripTypeOptions[1] : tripTypeOptions[0])
    }, [searchQuery, isSearchApiLoading, searchData])

    useEffect(() => {
        const raw =
            (Array.isArray(searchQuery?.sft) ? searchQuery.sft[0] : searchQuery?.sft) ??
            (Array.isArray(searchParams?.sft) ? searchParams.sft[0] : searchParams?.sft)
        setActiveFare(chipSftFromQuery(raw))
    }, [searchQuery?.sft, searchParams?.sft])

    useEffect(() => {
        if (!isEmpty(dropdownOpenType)) {
            document?.body?.classList?.add('stopScrolling')
        } else {
            document?.body?.classList?.remove('stopScrolling')
        }
        return () => {
            document?.body?.classList?.remove('stopScrolling')
        }
    }, [dropdownOpenType])

    const fareFilter = (enabled: boolean) => {
        return FARE_TYPE.map(item => {
            if (item.code === 'senior_citizen' || item.code === 'student') {
                return {
                    ...item,
                    active: enabled,
                    toolTipErrorMsg: !enabled ? `${item.fare} fares are available for adult passengers only. Please de-select children/infant passengers to continue.` : ''
                }
                //
            }
            return { ...item, active: true }
        })
    }

    useEffect(() => {
        const hasChildrenOrInfants = parseInt(query.childs) > 0 || parseInt(query.infants) > 0
        
        if (hasChildrenOrInfants) {
            setFareList(fareFilter(false))
            if (activeFare === 'student' || activeFare === 'senior_citizen') {
                setActiveFare('')
            }
        } else {
            setFareList(fareFilter(true))
        }
    }, [query.childs, query.infants])

    useEffect(() => {
        let departureIntl = false
        let arrivalIntl = false
        
        // Only check international status if cities have been actually selected (not just typed)
        const hasOnwardCity = !isEmpty(onwardCityValue.code)
        const hasReturnCity = !isEmpty(returnCityValue.code)
        
        const intlFromSearch = intl || query.intl
        if (hasOnwardCity && hasReturnCity) {
            departureIntl = isLegInternational(onwardCityDisplayValue, intlFromSearch)
            arrivalIntl = isLegInternational(returnCityDisplayValue, intlFromSearch)
        }
        setIsIntlArray([departureIntl, arrivalIntl])
        
        const isInternational = departureIntl || arrivalIntl
        
        if (isInternational) {
            const hasChildrenOrInfants = parseInt(query.childs) > 0 || parseInt(query.infants) > 0
            if (sft !== 'student' || hasChildrenOrInfants) {
                setActiveFare('')
            }
            const disabledFareList = fareFilter(!hasChildrenOrInfants).map(fare =>
                fare.active
                    ? { ...fare, toolTipErrorMsg: 'Special fares like student, senior citizen and armed forces are applicable only for domestic flights' }
                    : fare
            )
            setFareList(disabledFareList)
            // CFW is supported for international flights now; keep the selection intact.
        } else {
            const hasChildrenOrInfants = parseInt(query.childs) > 0 || parseInt(query.infants) > 0
            if (!hasChildrenOrInfants) {
                setFareList(FARE_TYPE)
            }
        }
    }, [
        onwardCityValue.code,
        returnCityValue.code,
        onwardCityDisplayValue,
        returnCityDisplayValue,
        isCfwSelected,
        intl,
        query.intl
    ])

    async function getAirportList(searchValue: string, type: LegType) {
        try {
            if (searchValue) {
                setSuggestionLoading(true)
                let searchResult = await airportSearch(searchValue)
                if (Array.isArray(searchResult)) {
                    setAirportSearchList({
                        ...airportSearchList,
                        [type]: searchResult || []
                    })
                }
                setSuggestionLoading(false)
            }
        } catch (e) {
            setSuggestionLoading(false)
            setAirportSearchList({
                ...airportSearchList,
                [type]: []
            })
            console.log('airportList api error', e)
        }
    }

    const openDropDown = (dropDownType: DropdownType) => {
        setTimeout(() => {
            setDropdownOpenType(dropDownType)
        })
    }
    const closeDropdown = () => {
        setDropdownOpenType('')
    }

    const onJourneyDropdownClick = (journeyType: LegType) => {
        openDropDown(journeyType)
    }

    const debouncedGetAirportList = useMemo(() => debounce(getAirportList, 400), [airportSearchList])

    const ontripTypeChange = (key: string) => {
        const tripType = tripTypeOptions.find(trip => trip.key === key)
        if (tripType) {
            closeDropdown()
            setTripType({
                ...tripType
            })
            if (tripType.key === 'OW') {
                setQuery({
                    ...query,
                    return_date: ''
                })
            } else {
                setQuery({
                    ...query,
                    return_date: query.depart_date
                })
            }
        }
    }

    const getCityDisplayValue = (airportCode: string, airportCityName: string) => {
        const cityName = (airportCityName || '').split('-')[0]?.trim()
        const cityDisplayValue = `${airportCode} - ${cityName}`
        return cityDisplayValue
    }

    const getCityOptionsList = (legType: LegType) => {
        let airportList = airportSearchList[legType]
        if (legType === ONWARD && showOnwardRecentSearchList) {
            airportList = recentSearchList
        }
        if (legType === RETURN && showReturnRecentSearchList) {
            airportList = recentSearchList
        }
        if (isEmpty(airportList)) {
            return []
        } else {
            const list = airportList.map(airportDetails => {
                const { pr } = airportDetails
                return {
                    key: airportDetails.k,
                    label: pr ? `\u00A0\u00A0\u00A0-\u00A0${airportDetails.v}` : `${airportDetails.v}`
                }
            })
            return list
        }
    }

    const selectCity = (key: string, legType: LegType) => {
        if (!suggestionLoading) {
            const airportList = getCityOptionsList(legType)
            const selectedAirport = airportList.find(airport => airport.key === key) || { key: '', label: '' }
            const airportCityName: string = selectedAirport.label
            const airportCode: string = selectedAirport.key
            const cityName = airportCityName?.split(' -')?.[0]?.split(',')?.[0] || ''
            const displayValue = getCityDisplayValue(airportCode, airportCityName)
            updateRecentSearch(airportCode, airportCityName)

            if (legType === ONWARD) {
                setOnwardCityValue({
                    code: displayValue,
                    name: cityName
                })
                setOnwardCityDisplayValue(displayValue)
            } else {
                setReturnCityValue({
                    code: displayValue,
                    name: cityName
                })
                setReturnCityDisplayValue(displayValue)
            }
            closeDropdown()
        }
    }

    const onCityChange = (e: React.ChangeEvent<HTMLInputElement>, legType: LegType) => {
        const { value } = e?.target
        if (isEmpty(value)) {
            setRecentSearchList(getRecentSearch())
            if (legType === ONWARD) {
                setShowOnwardRecentSearchList(true)
                setOnwardCityValue({
                    ...onwardCityValue,
                    code: ''
                })
            } else {
                setShowReturnRecentSearchList(true)
                setReturnCityValue({
                    ...returnCityValue,
                    code: ''
                })
            }
        } else {
            if (legType === ONWARD) {
                setShowOnwardRecentSearchList(false)
                debouncedGetAirportList(value, ONWARD)
                setOnwardCityValue({
                    ...onwardCityValue,
                    code: ''
                })
            } else {
                setShowReturnRecentSearchList(false)
                debouncedGetAirportList(value, RETURN)
                setReturnCityValue({
                    ...returnCityValue,
                    code: ''
                })
            }
        }
        if (legType === ONWARD) {
            setOnwardCityDisplayValue(value)
        } else {
            setReturnCityDisplayValue(value)
        }
    }

    const getOptionsListForOnwardAndReturn = (legType: LegType) => {
        if (suggestionLoading) {
            return [
                {
                    key: 'loader',
                    label: (
                        <Container className="w-100p flex flex-middle flex-center">
                            <Spinner />
                        </Container>
                    )
                }
            ]
        }
        return getCityOptionsList(legType)
    }

    const swapRoute = () => {
        if (onwardCityDisplayValue && returnCityDisplayValue) {
            let swapValue = !swapped
            setSwapped(swapValue)
            setOnwardCityDisplayValue(returnCityDisplayValue)
            setReturnCityDisplayValue(onwardCityDisplayValue)
            setOnwardCityValue(returnCityValue)
            setReturnCityValue(onwardCityValue)
            setTimeout(() => {
                setSwapped(!swapValue)
            }, 500)
        }
    }

    const dateClick = date => {
        if (date.to) {
            ontripTypeChange('RT')
            setQuery({
                ...query,
                depart_date: getFormattedDate(date.from, 'DD/MM/YYYY'),
                return_date: getFormattedDate(date.to, 'DD/MM/YYYY')
            })
        } else {
            setQuery({
                ...query,
                depart_date: getFormattedDate(date.from, 'DD/MM/YYYY')
            })
        }
    }

    const getSftQueryType = (queryObject = {}) => {
        if (queryObject?.intl !== 'y') {
            return activeFare || ''
        } else if (activeFare === 'student') {
            return activeFare
        }
        return ''
    }

    function modifySearchHandler() {
        if (isCfwSelected && !isUserSignedIn()) {
            setCfwModal(true)
            return
        }

        let queryObject = {
            ...searchParams,
            ...query
        }
        const statsigExp = Statsig.getExperiment(STATSIG_SPLIT_RT_EXP_NAME)?.get('variant', 'v1') as string
        queryObject.intl = 'n'
        let onwardCityType = onwardCityValue.code?.split(', ')[1]?.trim() || ''
        let returnCityType = returnCityValue.code?.split(', ')[1]?.trim() || ''
        if (onwardCityType !== 'IN' || returnCityType !== 'IN') {
            queryObject.intl = 'y'
        }
        queryObject.from = (onwardCityValue.code || '').split(' -')[0]
        queryObject.to = (returnCityValue.code || '').split(' -')[0]
        queryObject.origin = onwardCityValue.code
        queryObject.destination = returnCityValue.code
        queryObject.sourceCountry = onwardCityValue.name
        queryObject.destinationCountry = returnCityValue.name
        queryObject.rnd_one = query.return_date ? 'R' : 'O'
        queryObject.sft = getSftQueryType(queryObject)
        queryObject.isCfw = isCfwSelected ? isCfwSelected : false
        queryObject.isFF = queryObject.intl === 'y' && isNewInternational({ statsigExp })

        let showToast = modifyErrorHandler(queryObject) // handleToast
        if (showToast.validate) {
            setShowToast({
                isVisible: true,
                message: showToast.msg
            })
            setTimeout(() => {
                setShowToast({
                    isVisible: false,
                    message: ''
                })
            }, 3000)
        } else {
            closeDropdown()
            onSearchClick(queryObject)
        }
    }

    const modifyErrorHandler = queryObject => {
        let validate = false
        let msg = ''
        if (queryObject.from == queryObject.to) {
            msg = SAME_CITY_ERROR
            validate = true
        }
        return { validate, msg }
    }

    const onReturnCrossClick = () => {
        ontripTypeChange('OW')
    }
    const calculateDateFromDx = (dxValue: string) => {
        const daysToAdd = parseInt(dxValue, 10);
        if (isNaN(daysToAdd)) {
          return null;
        }
        return dayjs().add(daysToAdd, 'day').format('DD/MM/YYYY');
      };

    const widgetRowClassName = isSrpRevamp ? 'flex' : 'flex flex-center'
    const legacyFormMarginLeft = fareType === 'CFW' ? '60px' : '-68px'
    const formMarginLeft = isSrpRevamp ? 0 : legacyFormMarginLeft
    const fieldGapClassName = isSrpRevamp ? 'ml-4' : 'ml-3'
    const departureFieldClassName = isSrpRevamp ? 'p-relative ml-4' : 'p-relative ml-3'
    const searchButtonClassName = isSrpRevamp ? 'ba-solid br-8 bc-line-500 ml-4' : 'ba-solid br-8 bc-line-500 ml-3'
    const revampFieldCss = isSrpRevamp ? { padding: '12px' } : {}
    const revampTravellersFieldCss = isSrpRevamp ? { ...revampFieldCss, columnGap: 3 } : {}

    return (
        <>
            <Container className={widgetRowClassName}>
                <Container
                    className="flex flex-column"
                    style={{ gap: '16px', marginLeft: formMarginLeft }}
                >
                    {/* Row 1: Search Form */}
                    <Container className="flex flex-middle">
                    <Container className="p-relative">
                        <TextField
                            textTypographyProps={isSrpRevamp ? { variant: 'B2' } : undefined}
                            width={isSrpRevamp ? SRP_REVAMP_FIELD_WIDTH.tripType : 140}
                            size={'sm'}
                            select={true}
                            placeholder="Trip type"
                            value={tripType.label}
                            name={TRIP_TYPE}
                            styleConfig={{ root: { css: { ...glassFieldCss, ...revampFieldCss } } }}
                            onClick={() => {
                                if (dropdownOpenType === TRIP_TYPE) {
                                    closeDropdown()
                                } else {
                                    openDropDown(TRIP_TYPE)
                                }
                            }}
                            suffix={
                                <Container
                                    height="20px"
                                    style={{ rotate: dropdownOpenType === TRIP_TYPE ? '-180deg' : '0deg' }}
                                    {...(isSrpRevamp ? {} : { width: '20px' })}
                                >
                                    <DownChevronNew />
                                </Container>
                            }
                        />
                        {dropdownOpenType === TRIP_TYPE ? (
                            <OutsideClickHandler onOutsideClick={closeDropdown}>
                                <Container className="p-absolute l-0 r-0" style={{ top: '42px', zIndex: 30 }}>
                                    <Dropdown 
                                        optionList={tripTypeOptions} 
                                        onClick={ontripTypeChange}
                                        styleConfig={{
                                            dropdownContainer:{
                                                css:{overflow: 'auto'}
                                            }
                                        }}
                                    />
                                </Container>
                            </OutsideClickHandler>
                        ) : (
                            ''
                        )}
                    </Container>

                    <Container className={departureFieldClassName}>
                        <TextField
                            css={isSrpRevamp ? undefined : { width: '180px' }}
                            textTypographyProps={isSrpRevamp ? { variant: 'B2' } : undefined}
                            width={isSrpRevamp ? SRP_REVAMP_FIELD_WIDTH.airport : undefined}
                            placeholder="Departure"
                            value={onwardCityDisplayValue}
                            onClick={() => onJourneyDropdownClick(ONWARD)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onCityChange(e, ONWARD)}
                            focusColor={isDepartCityError ? ERROR_COLOR_CODE : ''}
                            height={40}
                            styleConfig={{
                                rootBox: {
                                    className: swapped ? 'swap-left' : ''
                                },
                                root: {
                                    css: {
                                        ...departCityColorCode,
                                        ...glassFieldCss,
                                        ...revampFieldCss
                                    }
                                }
                            }}
                        />
                        {dropdownOpenType === ONWARD ? (
                            <OutsideClickHandler onOutsideClick={closeDropdown}>
                                <Container className="p-absolute l-0" css={{ minWidth: '360px', top: '42px', zIndex: 30 }}>
                                    <Dropdown
                                        optionList={getOptionsListForOnwardAndReturn(ONWARD)}
                                        onClick={(key: string) => selectCity(key, ONWARD)}
                                        styleConfig={{
                                            dropdownContainer: {
                                                className: 'p-1'
                                            },
                                            listDropdown: {
                                                rootcontainer: {
                                                    className: 'mx-2'
                                                }
                                            }
                                        }}
                                    />
                                </Container>
                            </OutsideClickHandler>
                        ) : (
                            ''
                        )}
                    </Container>

                    <Container className={`c-pointer ml-3 ${swapped ? 'rotate180' : ''}`} onClick={swapRoute}>
                        <Swap />
                    </Container>

                    <Container className="p-relative ml-3">
                        <TextField
                            css={isSrpRevamp ? undefined : { width: '180px' }}
                            textTypographyProps={isSrpRevamp ? { variant: 'B2' } : undefined}
                            width={isSrpRevamp ? SRP_REVAMP_FIELD_WIDTH.airport : undefined}
                            className="br-8 py-2 flex flex-between flex-middle ws-nowrap"
                            value={returnCityDisplayValue}
                            focusColor={isArrivalCityError ? ERROR_COLOR_CODE : ''}
                            onClick={() => onJourneyDropdownClick(RETURN)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onCityChange(e, RETURN)}
                            height={40}
                            styleConfig={{
                                rootBox: {
                                    className: swapped ? 'swap-right' : ''
                                },
                                root: {
                                    css: {
                                        ...arrivalCityColorCode,
                                        ...glassFieldCss,
                                        ...revampFieldCss
                                    }
                                }
                            }}
                        />
                        {dropdownOpenType === RETURN ? (
                            <OutsideClickHandler onOutsideClick={closeDropdown}>
                                <Container className="p-absolute l-0" css={{ minWidth: '360px', top: '42px', zIndex: 30 }}>
                                    <Dropdown
                                        optionList={getOptionsListForOnwardAndReturn(RETURN)}
                                        onClick={(key: string) => selectCity(key, RETURN)}
                                    />
                                </Container>
                            </OutsideClickHandler>
                        ) : (
                            ''
                        )}
                    </Container>

                    <DateRangePicker
                        roundTrip={tripType.key === 'RT' ? true : false}
                        onwardJourny={onwardCityValue.name}
                        returnJourny={returnCityValue.name}
                        toCode={returnCityValue?.code?.split(' -')[0]}
                        fromCode={onwardCityValue?.code?.split(' -')[0]}
                        departDate={
                            query.depart_date === 'Invalid Date' || isEmpty(query.depart_date)
                                ? (isEmpty(depart_date) && dx) ? calculateDateFromDx(dx) : getFormattedDate(new Date(), 'DD/MM/YYYY')
                                : query.depart_date
                        }
                        returnDate={
                            query.return_date === 'Invalid Date'
                                ? getFormattedDate(new Date(), 'DD/MM/YYYY')
                                : query.return_date
                        }
                        dateClick={dateClick}
                        searchParams={query}
                        onReturnCrossClick={onReturnCrossClick}
                        showDateFare={true}
                        isCFW={isCfwSelected}
                        fieldWidth={isSrpRevamp ? SRP_REVAMP_FIELD_WIDTH.date : undefined}
                        textVariant={isSrpRevamp ? 'B2' : ''}
                        gapClassName={fieldGapClassName}
                        fieldCss={revampFieldCss}
                        isThemed={isThemed}
                    />
                    <Travellers
                        activeFare={activeFare}
                        closeDropdown={closeDropdown}
                        fieldCss={revampTravellersFieldCss}
                        fieldWidth={isSrpRevamp ? SRP_REVAMP_FIELD_WIDTH.travellers : undefined}
                        gapClassName={fieldGapClassName}
                        iconSize={isSrpRevamp ? 20 : undefined}
                        isIntl={isIntl}
                        isOpen={dropdownOpenType === TRAVELLERS}
                        isThemed={isThemed}
                        openDropDown={openDropDown}
                        query={query}
                        setQuery={setQuery}
                        textVariant={isSrpRevamp ? 'B2' : ''}
                    />
                    <Button
                        css={{ width: '128px', height: '42px' }}
                        className={searchButtonClassName}
                        onClick={modifySearchHandler}
                        disabled={isSearchButtonDisabled}
                    >
                        <Typography variant="L2" color={isSearchButtonDisabled ? 'tertiary' : 'neutral'} className="c-pointer">
                            Search
                        </Typography>
                    </Button>
                </Container>

                    <Container className="flex flex-middle">
                        <SpecialFaresRow
                            isIntl={isIntl}
                            activeFare={activeFare}
                            setActiveFare={setActiveFare}
                            setIsCfwSelected={setIsCfwSelected}
                            isCfwSelected={isCfwSelected}
                            cfwModal={cfwModal}
                            setCfwModal={setCfwModal}
                            fareTypeList={fareList}
                            tripType={tripType.key === 'RT' ? 'R' : 'O'}
                            variant="srp"
                            searchHandler={modifySearchHandler}
                            isRevamp={isSrpRevamp}
                            isThemed={isThemed}
                            isDarkThemed={isDarkThemed}
                        />
                    </Container>
                </Container>
            </Container>

            <Toast
                isVisible={showToast.isVisible}
                message={showToast.message}
                containerClass="bg-error br-4 p-4 r-0 l-0 p-fixed"
            />
            
            {!isEmpty(dropdownOpenType) &&
                ReactDOM.createPortal(<Overlay customOverlayWrapperClass="z-20" />, document.body)}
        </>
    )
}

export default ModifySearchWidget
